import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { delay, map, tap, catchError } from 'rxjs/operators';
import { 
  EnhancedScheduleEvent, 
  RecurrencePattern, 
  ScheduleConflict, 
  ScheduleTemplate,
  Reminder,
  Notification 
} from '../models/enhanced-schedule-event.model';
import { environment } from '../../../environments/environment';

// API Models (matching C# API)
export interface SchedulerAddModel {
  name: string;
  groupId?: number;
  description?: string;
}

export interface SchedulerEditModel {
  id: number;
  name: string;
  groupId?: number;
  description?: string;
  folderId?: number | null;
  screenId?: number | null;
  timeZone?: string;
}

export interface SchedulerChangeStatusModel {
  id: number;
  status: boolean;
}

export interface EventAddModel {
  schedulerId: number;
  name: string;
  description?: string;
  eventType: number;
  cronExpression?: string;
  cronExpressionProperties?: string;
  timeZone?: string;
  startDate: string;
  endDate: string;
  userMediaAppId: number;
  displayListId?: number;
  userId: string;
}

export interface EventEditModel extends EventAddModel {
  id: number;
}

export interface SchedulerListItemDto {
  id: number;
  name: string;
  groupId?: number;
  groupName?: string;
  status: boolean;
  createdDate: string;
  eventCount: number;
}

export interface SchedulerForEditDto {
  id: number;
  name: string;
  groupId?: number;
  description?: string;
  status: boolean;
  events?: any[]; // Events associated with this scheduler
}

export interface SelectListItemDto {
  value: number;
  text: string;
}

export interface FilterModel {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface SchedulerFilterModel extends FilterModel {
  groupId?: number;
  status?: boolean;
}

export interface SchedulerPagedResultModel<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class EnhancedSchedulerService {
  private readonly apiUrl = `${environment.apiBaseUrl}api/scheduler`;
  
  private eventsSubject = new BehaviorSubject<EnhancedScheduleEvent[]>([]);
  private templatesSubject = new BehaviorSubject<ScheduleTemplate[]>([]);
  private conflictsSubject = new BehaviorSubject<ScheduleConflict[]>([]);

  public events$ = this.eventsSubject.asObservable();
  public templates$ = this.templatesSubject.asObservable();
  public conflicts$ = this.conflictsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Scheduler API Methods (matching C# API endpoints)
  
  /**
   * Add a new scheduler
   * POST /api/scheduler/add
   */
  addScheduler(model: SchedulerAddModel): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/add`, model)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get paged result of schedulers
   * GET /api/scheduler/paged-result
   */
  getPagedResult(params: FilterModel | any): Observable<SchedulerPagedResultModel<SchedulerListItemDto>> {
    return this.http.get<SchedulerPagedResultModel<SchedulerListItemDto>>(`${this.apiUrl}/paged-result`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get paged list of schedulers with filtering
   * GET /api/scheduler/paged-list
   */
  getPagedList(params: SchedulerFilterModel | any): Observable<SchedulerPagedResultModel<SchedulerListItemDto>> {
    return this.http.get<SchedulerPagedResultModel<SchedulerListItemDto>>(`${this.apiUrl}/paged-list`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get scheduler for editing
   * GET /api/scheduler/get-for-edit/{id}
   */
  getSchedulerForEdit(id: number): Observable<SchedulerForEditDto> {
    return this.http.get<SchedulerForEditDto>(`${this.apiUrl}/get-for-edit/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Edit an existing scheduler
   * POST /api/scheduler/edit
   */
  editScheduler(model: SchedulerEditModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/edit`, model)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Change scheduler status
   * POST /api/scheduler/change-status
   */
  changeSchedulerStatus(model: SchedulerChangeStatusModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/change-status`, model)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete scheduler
   * POST /api/scheduler/delete/{id}
   */
  deleteScheduler(id: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/delete/${id}`, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get select list items
   * GET /api/scheduler/get-select-list-items/{groupId?}
   */
  getSelectListItems(groupId?: number): Observable<SelectListItemDto[]> {
    const url = groupId ? `${this.apiUrl}/get-select-list-items/${groupId}` : `${this.apiUrl}/get-select-list-items`;
    return this.http.get<SelectListItemDto[]>(url)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get select list items for group edit
   * GET /api/scheduler/get-select-list-items-for-group-edit/{groupId}
   */
  getSelectListItemsForGroupEdit(groupId: number): Observable<SelectListItemDto[]> {
    return this.http.get<SelectListItemDto[]>(`${this.apiUrl}/get-select-list-items-for-group-edit/${groupId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get list of schedulers
   * GET /api/scheduler/list/{groupId?}
   */
  getSchedulerList(groupId?: number, filterModel?: FilterModel): Observable<SchedulerListItemDto[]> {
    let params = new HttpParams();
    if (filterModel?.page) params = params.set('page', filterModel.page.toString());
    if (filterModel?.pageSize) params = params.set('pageSize', filterModel.pageSize.toString());
    if (filterModel?.searchTerm) params = params.set('searchTerm', filterModel.searchTerm);
    if (filterModel?.sortBy) params = params.set('sortBy', filterModel.sortBy);
    if (filterModel?.sortDirection) params = params.set('sortDirection', filterModel.sortDirection);

    const url = groupId ? `${this.apiUrl}/list/${groupId}` : `${this.apiUrl}/list`;
    return this.http.get<SchedulerListItemDto[]>(url, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Add an event to a scheduler
   * POST /api/scheduler/event/add
   */
  addEvent(model: EventAddModel): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/event/add`, model)
      .pipe(
        catchError(this.handleError)
      );
  }
  UpdateEvent(model: EventEditModel): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/event/edit`, model)
      .pipe(
        catchError(this.handleError)
      );
  }
  CopyScheduler(model: any): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/copy`, model)
      .pipe(
        catchError(this.handleError)
      );
  }
  DeleteScheduler(id: any): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/delete/${id}`, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get events for a specific scheduler
   * GET /api/scheduler/event/list/{schedulerId}
   */
  getEventsBySchedulerId(schedulerId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/get-for-edit/${schedulerId}`)
      .pipe(
        catchError(this.handleError)
      );
  }
    exists(filterModel: FilterModel, folderId?: number): Observable<any> {
        if (!filterModel['groupId'] || filterModel['groupId']  === -1) {
            filterModel['groupId'] = null;
        }
        if(folderId == undefined) {
            folderId = null;
        }
        return this.http.get(`${this.apiUrl}/exists/${filterModel['groupId']}/${folderId}?mineOnly=${filterModel['mineOnly']}`);
    }
  /**
   * Get paged events for a specific scheduler
   * GET /api/scheduler/event/paged-list
   */
  getPagedEventsBySchedulerId(schedulerId: number, pageIndex: number = 1, pageSize: number = 50): Observable<any> {
    const params = new HttpParams()
      .set('schedulerId', schedulerId.toString())
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.apiUrl}/event/paged-list`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Error handler
   */
  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    let errorMessage = 'An error occurred';
    
    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (Array.isArray(error.error)) {
        errorMessage = error.error.join(', ');
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return throwError(errorMessage);
  }

  // Legacy methods for backward compatibility
  private mockEvents: EnhancedScheduleEvent[] = [
    {
      id: '1',
      title: 'Morning Promotional Content',
      description: 'Display promotional videos during morning hours',
      startDate: new Date(2025, 9, 16, 8, 0),
      endDate: new Date(2025, 9, 16, 12, 0),
      isAllDay: false,
      timeZone: 'America/New_York',
      contentType: 'playlist',
      contentId: 'promo-playlist-1',
      deviceIds: ['device-1', 'device-2'],
      reminders: [
        {
          id: 'rem-1',
          type: 'popup',
          minutesBefore: 15,
          message: 'Morning content starts in 15 minutes',
          isEnabled: true
        }
      ],
      notifications: [],
      priority: 'normal',
      color: '#1890ff',
      status: 'active',
      visibility: 'public',
      createdAt: new Date(),
      recurrence: {
        type: 'daily',
        interval: 1,
        endType: 'never'
      }
    }
  ];

  private mockTemplates: ScheduleTemplate[] = [
    {
      id: 'temp-1',
      name: 'Daily Promotional Schedule',
      description: 'Standard promotional content for weekdays',
      category: 'Promotional',
      template: {
        contentType: 'playlist',
        priority: 'normal',
        isAllDay: false,
        visibility: 'public',
        recurrence: {
          type: 'daily',
          interval: 1,
          daysOfWeek: [1, 2, 3, 4, 5],
          endType: 'never'
        }
      }
    },
    {
      id: 'temp-2',
      name: 'Weekend Special Events',
      description: 'Special content for weekends',
      category: 'Events',
      template: {
        contentType: 'media',
        priority: 'high',
        isAllDay: true,
        visibility: 'public',
        recurrence: {
          type: 'weekly',
          interval: 1,
          daysOfWeek: [0, 6],
          endType: 'never'
        }
      }
    }
  ];



  // Event CRUD Operations
  getAllEvents(): Observable<EnhancedScheduleEvent[]> {
    return of(this.mockEvents).pipe(delay(500));
  }

  getEventById(id: string): Observable<EnhancedScheduleEvent | null> {
    const event = this.mockEvents.find(e => e.id === id) || null;
    return of(event).pipe(delay(300));
  }

  getEventsByDateRange(startDate: Date, endDate: Date): Observable<EnhancedScheduleEvent[]> {
    const filtered = this.mockEvents.filter(event => {
      return event.startDate >= startDate && event.startDate <= endDate;
    });
    return of(filtered).pipe(delay(300));
  }

  // Create a new scheduler (uses actual API)
  createSchedule(scheduleData: SchedulerAddModel): Observable<{ id: number; name: string }> {
    return this.addScheduler(scheduleData).pipe(
      map(id => ({ id, name: scheduleData.name }))
    );
  }

  // Create a new event (uses actual API)
  createEvent(eventData: EventAddModel): Observable<number> {
    return this.addEvent(eventData);
  }

  // Legacy method for backward compatibility
  createEnhancedEvent(event: Partial<EnhancedScheduleEvent>): Observable<EnhancedScheduleEvent> {
    const newEvent: EnhancedScheduleEvent = {
      id: `event-${Date.now()}`,
      title: event.title || 'Untitled Event',
      startDate: event.startDate || new Date(),
      endDate: event.endDate || new Date(),
      isAllDay: event.isAllDay || false,
      timeZone: event.timeZone || 'UTC',
      contentType: event.contentType || 'media',
      deviceIds: event.deviceIds || [],
      reminders: event.reminders || [],
      notifications: event.notifications || [],
      priority: event.priority || 'normal',
      status: 'active',
      visibility: event.visibility || 'public',
      createdAt: new Date(),
      updatedAt: new Date(),
      ...event
    };

    // Check for conflicts
    this.checkConflicts(newEvent).subscribe(conflicts => {
      if (conflicts.length > 0) {
        this.conflictsSubject.next(conflicts);
      }
    });

    this.mockEvents.push(newEvent);
    this.eventsSubject.next([...this.mockEvents]);
    
    return of(newEvent).pipe(delay(500));
  }

  updateEvent(id: string, updates: Partial<EnhancedScheduleEvent>): Observable<EnhancedScheduleEvent> {
    const index = this.mockEvents.findIndex(e => e.id === id);
    if (index === -1) {
      return throwError('Event not found');
    }

    const updatedEvent = {
      ...this.mockEvents[index],
      ...updates,
      updatedAt: new Date()
    };

    this.mockEvents[index] = updatedEvent;
    this.eventsSubject.next([...this.mockEvents]);
    
    return of(updatedEvent).pipe(delay(500));
  }

  deleteEvent(id: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/event/delete/${id}`, {})
      .pipe(
        map(() => true),
        catchError(error => {
          console.error('Delete event error:', error);
          return of(false);
        })
      );
  }

  duplicateEvent(id: string): Observable<EnhancedScheduleEvent> {
    const originalEvent = this.mockEvents.find(e => e.id === id);
    if (!originalEvent) {
      return throwError('Event not found');
    }

    const duplicatedEvent: EnhancedScheduleEvent = {
      ...originalEvent,
      id: `event-${Date.now()}`,
      title: `${originalEvent.title} (Copy)`,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return this.createEnhancedEvent(duplicatedEvent);
  }

  // Recurrence Handling
  expandRecurringEvents(event: EnhancedScheduleEvent, startDate: Date, endDate: Date): EnhancedScheduleEvent[] {
    if (!event.recurrence || event.recurrence.type === 'none') {
      return [event];
    }

    const instances: EnhancedScheduleEvent[] = [];
    const pattern = event.recurrence;
    let currentDate = new Date(event.startDate);
    let occurrenceCount = 0;

    while (currentDate <= endDate) {
      if (currentDate >= startDate) {
        // Check if this date should be excluded
        const isException = pattern.exceptions?.some(exception => 
          this.isSameDay(exception, currentDate)
        );

        if (!isException) {
          const instance: EnhancedScheduleEvent = {
            ...event,
            id: `${event.id}-${currentDate.getTime()}`,
            startDate: new Date(currentDate),
            endDate: new Date(currentDate.getTime() + (event.endDate.getTime() - event.startDate.getTime()))
          };
          instances.push(instance);
        }
      }

      // Calculate next occurrence
      currentDate = this.getNextOccurrence(currentDate, pattern);
      occurrenceCount++;

      // Check end conditions
      if (pattern.endType === 'after' && occurrenceCount >= (pattern.endAfterOccurrences || 0)) {
        break;
      }
      if (pattern.endType === 'until' && pattern.endDate && currentDate > pattern.endDate) {
        break;
      }
    }

    return instances;
  }

  private getNextOccurrence(date: Date, pattern: RecurrencePattern): Date {
    const next = new Date(date);

    switch (pattern.type) {
      case 'daily':
        next.setDate(next.getDate() + pattern.interval);
        break;
      case 'weekly':
        next.setDate(next.getDate() + (7 * pattern.interval));
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + pattern.interval);
        break;
      case 'yearly':
        next.setFullYear(next.getFullYear() + pattern.interval);
        break;
    }

    return next;
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  // Conflict Detection
  checkConflicts(event: EnhancedScheduleEvent): Observable<ScheduleConflict[]> {
    const conflicts: ScheduleConflict[] = [];

    // Check for time overlaps on same devices
    this.mockEvents.forEach(existingEvent => {
      if (existingEvent.id === event.id) return;

      const hasDeviceOverlap = event.deviceIds.some(deviceId => 
        existingEvent.deviceIds.includes(deviceId)
      );

      if (hasDeviceOverlap) {
        const hasTimeOverlap = this.hasTimeOverlap(event, existingEvent);
        
        if (hasTimeOverlap) {
          conflicts.push({
            id: `conflict-${Date.now()}`,
            conflictType: 'time-overlap',
            severity: 'warning',
            message: `Time overlap detected with "${existingEvent.title}"`,
            suggestedResolution: 'Adjust timing or use different devices',
            conflictingEvents: [existingEvent.id!]
          });
        }
      }
    });

    return of(conflicts).pipe(delay(200));
  }

  private hasTimeOverlap(event1: EnhancedScheduleEvent, event2: EnhancedScheduleEvent): boolean {
    return event1.startDate < event2.endDate && event1.endDate > event2.startDate;
  }

  // Template Management
  getTemplates(): Observable<ScheduleTemplate[]> {
    return of(this.mockTemplates).pipe(delay(300));
  }

  createTemplate(template: Omit<ScheduleTemplate, 'id'>): Observable<ScheduleTemplate> {
    const newTemplate: ScheduleTemplate = {
      ...template,
      id: `template-${Date.now()}`
    };

    this.mockTemplates.push(newTemplate);
    this.templatesSubject.next([...this.mockTemplates]);

    return of(newTemplate).pipe(delay(300));
  }

  createEventFromTemplate(templateId: string, overrides: Partial<EnhancedScheduleEvent>): Observable<EnhancedScheduleEvent> {
    const template = this.mockTemplates.find(t => t.id === templateId);
    if (!template) {
      return throwError('Template not found');
    }

    const eventData: Partial<EnhancedScheduleEvent> = {
      ...template.template,
      ...overrides
    };

    return this.createEnhancedEvent(eventData);
  }

  // Quick Add (Google Calendar style)
  quickAdd(quickText: string, defaultDate?: Date): Observable<EnhancedScheduleEvent> {
    // Parse quick text like "Meeting tomorrow 2pm-3pm"
    const parsed = this.parseQuickText(quickText, defaultDate);
    return this.createEnhancedEvent(parsed);
  }

  private parseQuickText(text: string, defaultDate?: Date): Partial<EnhancedScheduleEvent> {
    // Simple parsing logic - can be enhanced
    const today = defaultDate || new Date();
    
    return {
      title: text,
      startDate: today,
      endDate: new Date(today.getTime() + 60 * 60 * 1000), // 1 hour later
      isAllDay: false,
      timeZone: 'UTC',
      contentType: 'media',
      deviceIds: [],
      reminders: [],
      notifications: [],
      priority: 'normal'
    };
  }

  // Calendar View Helpers
  getEventsForMonth(year: number, month: number): Observable<EnhancedScheduleEvent[]> {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    return this.getEventsByDateRange(startDate, endDate);
  }

  getEventsForWeek(startOfWeek: Date): Observable<EnhancedScheduleEvent[]> {
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    return this.getEventsByDateRange(startOfWeek, endOfWeek);
  }

  getEventsForDay(date: Date): Observable<EnhancedScheduleEvent[]> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return this.getEventsByDateRange(startOfDay, endOfDay);
  }

  // Bulk Operations
  bulkUpdateEvents(eventIds: string[], updates: Partial<EnhancedScheduleEvent>): Observable<EnhancedScheduleEvent[]> {
    const updatedEvents: EnhancedScheduleEvent[] = [];

    eventIds.forEach(id => {
      const index = this.mockEvents.findIndex(e => e.id === id);
      if (index !== -1) {
        this.mockEvents[index] = {
          ...this.mockEvents[index],
          ...updates,
          updatedAt: new Date()
        };
        updatedEvents.push(this.mockEvents[index]);
      }
    });

    this.eventsSubject.next([...this.mockEvents]);
    return of(updatedEvents).pipe(delay(500));
  }

  bulkDeleteEvents(eventIds: string[]): Observable<boolean> {
    eventIds.forEach(id => {
      const index = this.mockEvents.findIndex(e => e.id === id);
      if (index !== -1) {
        this.mockEvents.splice(index, 1);
      }
    });

    this.eventsSubject.next([...this.mockEvents]);
    return of(true).pipe(delay(300));
  }

  // Import/Export
  exportEvents(format: 'ical' | 'json' | 'csv' = 'json'): Observable<string> {
    // Implementation would depend on format
    const data = JSON.stringify(this.mockEvents, null, 2);
    return of(data).pipe(delay(500));
  }

  importEvents(data: string, format: 'ical' | 'json' | 'csv' = 'json'): Observable<EnhancedScheduleEvent[]> {
    // Implementation would depend on format
    try {
      const events = JSON.parse(data) as EnhancedScheduleEvent[];
      events.forEach(event => {
        event.id = `imported-${Date.now()}-${Math.random()}`;
        this.mockEvents.push(event);
      });
      
      this.eventsSubject.next([...this.mockEvents]);
      return of(events).pipe(delay(500));
    } catch (error) {
      return throwError('Invalid data format');
    }
  }
      folderExists(groupId?: number, mineOnly?: boolean) {
        if (groupId == undefined || groupId == -1) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }

        return this.http.get(`${this.apiUrl}/has-folder/${groupId}?mineOnly=${mineOnly}`);
    }

    getFolderList(filterKey: string, groupId?: number, mineOnly?: boolean) {
        if (groupId == undefined) {
            groupId = null;
        }
        if (mineOnly == undefined) {
            mineOnly = null;
        }
        return this.http.get(`${this.apiUrl}/folders/${groupId}?mineOnly=${mineOnly}&filterKey=${filterKey}`);
    }
}